import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import { Card, Col, Row, Table, Tabs, Tab, Badge, Form, Button, Modal, Spinner, Dropdown } from "react-bootstrap";
import {
  X,
  Building2,
  UserX,
  Info,
  Eye,
  ShieldAlert,
} from "lucide-react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import PageTitle from "../layouts/PageTitle";
import Pagination from "../components/Pagination";
import TableActionsMenu from "../components/TableActionsMenu";
import {
  getPartner,
  deleteParnter,
  deleteUsers,
  blockUser,
  getSUBSCRIPTION,
  getDisableUserList,
  hardDeletePartner,
  hardDeleteUser,
} from "../../services/UserServices/UserService";


const svg1 = (
  <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <rect x="0" y="0" width="24" height="24"></rect>
      <circle fill="#000000" cx="12" cy="5" r="2"></circle>
      <circle fill="#000000" cx="12" cy="12" r="2"></circle>
      <circle fill="#000000" cx="12" cy="19" r="2"></circle>
    </g>
  </svg>
);



export default function DeletePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("partners");

  const todayDateObj = new Date();
  const todayStr = `${todayDateObj.getFullYear()}-${String(todayDateObj.getMonth() + 1).padStart(2, "0")}-${String(todayDateObj.getDate()).padStart(2, "0")}`;


  const [partnerList, setPartnerList] = useState([]);
  const [partnerTotalCount, setPartnerTotalCount] = useState(0);
  const [partnerPage, setPartnerPage] = useState(0);
  const [partnerItemsPerPage] = useState(10);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [partnerStartDate, setPartnerStartDate] = useState("");
  const [partnerEndDate, setPartnerEndDate] = useState("");
  const [partnerPackageFilter, setPartnerPackageFilter] = useState("");
  const [partnerSortField, setPartnerSortField] = useState("updatedAt");
  const [partnerSortOrder, setPartnerSortOrder] = useState("desc");
  const [partnerSortSubscriptionId, setPartnerSortSubscriptionId] = useState("");
  const [partnerLoading, setPartnerLoading] = useState(true);

  // ==================== TAB 2: DISABLED HOSTS (USERS) STATE ====================
  const [hostList, setHostList] = useState([]);
  const [hostTotalCount, setHostTotalCount] = useState(0);
  const [hostPage, setHostPage] = useState(0);
  const [hostItemsPerPage] = useState(10);
  const [hostSearch, setHostSearch] = useState("");
  const [hostStartDate, setHostStartDate] = useState("");
  const [hostEndDate, setHostEndDate] = useState("");
  const [hostUserType, setHostUserType] = useState("all");
  const [hostSortByPlan, setHostSortByPlan] = useState("");
  const [hostSortBy, setHostSortBy] = useState("updatedAt");
  const [hostSortOrder, setHostSortOrder] = useState("desc");
  const [hostLoading, setHostLoading] = useState(true);
  const [subscriptionsList, setSubscriptionsList] = useState([]);

  // ==================== ACTION MODALS STATE ====================
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [recordToRestore, setRecordToRestore] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const scrollPosRef = useRef(0);
  const tableScrollRef = useRef(null);

  // Fetch subscriptions list for filter dropdown
  useEffect(() => {
    async function fetchSubs() {
      try {
        const res = await getSUBSCRIPTION();
        setSubscriptionsList(res?.data?.data || []);
      } catch (err) {
        console.error("Error fetching subscriptions:", err);
      }
    }
    fetchSubs();
  }, []);

  // ==================== FETCH DISABLED PARTNERS (ISBLOCKED = TRUE) ====================
  const fetchDisabledPartners = useCallback(async () => {
    try {
      setPartnerLoading(true);
      const payload = {
        adminApproval: "accepted",
        page: partnerPage,
        limit: partnerItemsPerPage,
        isBlocked: true,
        ...(partnerSearch && { search: partnerSearch }),
        ...(partnerPackageFilter && { package: partnerPackageFilter }),
        ...(partnerStartDate && { startDate: partnerStartDate }),
        ...(partnerEndDate && { endDate: partnerEndDate }),
        ...(partnerSortField && {
          sortBy: {
            field: partnerSortField,
            order: partnerSortOrder || "asc",
            ...(partnerSortSubscriptionId && { subscriptionId: partnerSortSubscriptionId }),
          },
        }),
      };

      const response = await getPartner(payload);
      const resData = response?.data?.data || response?.data;
      const list = resData?.partners || resData?.docs || resData?.list || resData?.vendors || (Array.isArray(resData) ? resData : null);
      const count = resData?.count || resData?.total || resData?.totalCount || (list ? list.length : 0);

      if (list && Array.isArray(list)) {
        const processed = list.map((partner) => {
          const plans = partner?.partnerPlans || partner?.combinedPackages || partner?.plans || [];
          let essential = null;
          let premium = null;
          let elite = null;

          if (Array.isArray(plans)) {
            plans.forEach((p) => {
              const name = (
                p?.subscriptionId?.name ||
                p?.planId?.name ||
                p?.packageId?.name ||
                p?.name ||
                ""
              ).toString().trim().toLowerCase();

              if (name === "essential") essential = p;
              else if (name === "premium") premium = p;
              else if (name === "elite") elite = p;
            });
          }

          return {
            ...partner,
            essentialPlan: essential,
            premiumPlan: premium,
            elitePlan: elite,
          };
        });
        setPartnerList(processed);
        setPartnerTotalCount(count);
      } else {
        setPartnerList([]);
        setPartnerTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching disabled partners:", error);
      toast.error(error?.response?.data?.message || "Failed to fetch disabled partners");
    } finally {
      setPartnerLoading(false);
    }
  }, [
    partnerPage,
    partnerItemsPerPage,
    partnerSearch,
    partnerPackageFilter,
    partnerStartDate,
    partnerEndDate,
    partnerSortField,
    partnerSortOrder,
    partnerSortSubscriptionId,
  ]);

  // ==================== FETCH DISABLED HOSTS ====================
  const fetchDisabledHosts = useCallback(async () => {
    try {
      setHostLoading(true);
      const response = await getDisableUserList(
        hostPage,
        hostItemsPerPage,
        hostSearch,
        hostSortByPlan,
        hostStartDate,
        hostEndDate,
        "disabled",
        hostSortBy,
        hostSortOrder,
        hostUserType
      );
      const resData = response?.data?.data || response?.data;
      const list = resData?.users || resData?.docs || resData?.list || (Array.isArray(resData) ? resData : null);
      const count = resData?.count || resData?.total || (list ? list.length : 0);

      setHostList(list || []);
      setHostTotalCount(count || 0);
    } catch (error) {
      console.error("Error fetching disabled hosts:", error);
      toast.error(error?.response?.data?.message || "Failed to fetch disabled hosts");
    } finally {
      setHostLoading(false);
    }
  }, [
    hostPage,
    hostItemsPerPage,
    hostSearch,
    hostSortByPlan,
    hostStartDate,
    hostEndDate,
    hostSortBy,
    hostSortOrder,
    hostUserType,
  ]);

  // Sync Fetch Effects
  useEffect(() => {
    fetchDisabledPartners();
  }, [fetchDisabledPartners]);

  useEffect(() => {
    fetchDisabledHosts();
  }, [fetchDisabledHosts]);

  // Reset page to 0 on filter changes
  useEffect(() => {
    setPartnerPage(0);
  }, [partnerSearch, partnerStartDate, partnerEndDate, partnerPackageFilter, partnerSortField, partnerSortOrder, partnerSortSubscriptionId]);

  useEffect(() => {
    setHostPage(0);
  }, [hostSearch, hostStartDate, hostEndDate, hostUserType, hostSortByPlan, hostSortBy, hostSortOrder]);

  useLayoutEffect(() => {
    if (tableScrollRef.current && scrollPosRef.current > 0) {
      tableScrollRef.current.scrollLeft = scrollPosRef.current;
    }
  }, [partnerList, hostList, partnerLoading, hostLoading, activeTab]);

  useEffect(() => {
    scrollPosRef.current = 0;
  }, [activeTab]);

  const dynamicPlans = useMemo(() => {
    if (Array.isArray(subscriptionsList) && subscriptionsList.length > 0) {
      return subscriptionsList;
    }
    const map = new Map();
    if (Array.isArray(partnerList)) {
      partnerList.forEach((partner) => {
        const plans = partner?.partnerPlans || partner?.combinedPackages || partner?.plans || [];
        if (Array.isArray(plans)) {
          plans.forEach((p) => {
            const id = p?.subscriptionId?._id || p?.subscriptionId || p?.planId?._id || p?.planId || p?._id || p?.id;
            const name = p?.subscriptionId?.name || p?.planId?.name || p?.packageId?.name || p?.name;
            if (name && id && !map.has(id.toString())) {
              map.set(id.toString(), { _id: id, name });
            }
          });
        }
      });
    }
    return Array.from(map.values());
  }, [subscriptionsList, partnerList]);

  const renderPlanHeaders = () => {
    if (!dynamicPlans || dynamicPlans.length === 0) return null;
    return dynamicPlans.map((plan) => {
      const planId = plan._id || plan.id || plan.name;
      const planName = plan.name || plan.title || "Plan";
      return (
        <React.Fragment key={planId}>
          <th
            className="cursor-pointer"
            onClick={() => handlePartnerHeaderSort("partnerPlans.totalHostLimit", planId)}
          >
            <div><strong>{planName}</strong></div>
            <small className="text-muted fw-semibold">
              Consumed{" "}
              {partnerSortField === "partnerPlans.totalHostLimit" && partnerSortSubscriptionId === planId
                ? partnerSortOrder === "asc"
                  ? "↑"
                  : "↓"
                : "↕"}
            </small>
          </th>
          <th
            className="cursor-pointer"
            onClick={() => handlePartnerHeaderSort("partnerPlans.remainingHostLimit", planId)}
          >
            <div><strong>{planName}</strong></div>
            <small className="text-muted fw-semibold">
              Remaining{" "}
              {partnerSortField === "partnerPlans.remainingHostLimit" && partnerSortSubscriptionId === planId
                ? partnerSortOrder === "asc"
                  ? "↑"
                  : "↓"
                : "↕"}
            </small>
          </th>
        </React.Fragment>
      );
    });
  };

  const renderPlanCells = (partner) => {
    if (!dynamicPlans || dynamicPlans.length === 0) return null;
    const partnerPlansList = partner?.partnerPlans || partner?.combinedPackages || partner?.plans || [];
    return dynamicPlans.map((plan) => {
      const planId = plan._id || plan.id;
      const targetName = (plan.name || plan.title || "").toString().trim().toLowerCase();

      const matchedPlan = Array.isArray(partnerPlansList)
        ? partnerPlansList.find((p) => {
          const pId = p?.subscriptionId?._id || p?.subscriptionId || p?.planId?._id || p?.planId || p?._id || p?.id;
          const pName = (p?.subscriptionId?.name || p?.planId?.name || p?.packageId?.name || p?.name || "").toString().trim().toLowerCase();
          return (planId && pId && pId.toString() === planId.toString()) || (targetName && pName === targetName);
        })
        : null;

      return (
        <React.Fragment key={planId || plan.name}>
          <td>
            {matchedPlan
              ? `${(matchedPlan.totalHostLimit ?? matchedPlan.hostLimit ?? 0) - (matchedPlan.remainingHostLimit ?? 0)} / ${matchedPlan.totalHostLimit ?? matchedPlan.hostLimit ?? "-"}`
              : "-"}
          </td>
          <td>
            {matchedPlan ? (matchedPlan.remainingHostLimit ?? "-") : "-"}
          </td>
        </React.Fragment>
      );
    });
  };

  // Handlers for Partner Header Sort
  const handlePartnerHeaderSort = (field, subscriptionId = "") => {
    if (tableScrollRef.current) {
      scrollPosRef.current = tableScrollRef.current.scrollLeft;
    }
    if (partnerSortField === field && partnerSortSubscriptionId === subscriptionId) {
      setPartnerSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setPartnerSortField(field);
      setPartnerSortSubscriptionId(subscriptionId);
      setPartnerSortOrder("asc");
    }
  };

  // Handlers for Host Header Sort
  const handleHostHeaderSort = (field) => {
    if (tableScrollRef.current) {
      scrollPosRef.current = tableScrollRef.current.scrollLeft;
    }
    if (hostSortBy === field) {
      setHostSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setHostSortBy(field);
      setHostSortOrder("asc");
    }
  };

  // Date Change Handlers with Constraints
  const handlePartnerStartDateChange = (e) => {
    const val = e.target.value;
    if (val && val > todayStr) {
      toast.error("Start date cannot be greater than current date");
      return;
    }
    if (val && partnerEndDate && val > partnerEndDate) {
      toast.error("Start date cannot be greater than end date");
      setPartnerEndDate("");
    }
    setPartnerStartDate(val);
  };

  const handlePartnerEndDateChange = (e) => {
    const val = e.target.value;
    if (val && val > todayStr) {
      toast.error("End date cannot be greater than current date");
      return;
    }
    if (val && partnerStartDate && val < partnerStartDate) {
      toast.error("End date cannot be less than start date");
      return;
    }
    setPartnerEndDate(val);
  };

  const handleHostStartDateChange = (e) => {
    const val = e.target.value;
    if (val && val > todayStr) {
      toast.error("Start date cannot be greater than current date");
      return;
    }
    if (val && hostEndDate && val > hostEndDate) {
      toast.error("Start date cannot be greater than end date");
      setHostEndDate("");
    }
    setHostStartDate(val);
  };

  const handleHostEndDateChange = (e) => {
    const val = e.target.value;
    if (val && val > todayStr) {
      toast.error("End date cannot be greater than current date");
      return;
    }
    if (val && hostStartDate && val < hostStartDate) {
      toast.error("End date cannot be less than start date");
      return;
    }
    setHostEndDate(val);
  };

  // Restore / Unblock Handler
  const handleConfirmRestore = async () => {
    if (!recordToRestore) return;
    try {
      setRestoreLoading(true);
      const recordId = recordToRestore._id || recordToRestore.id;
      await blockUser({ userId: recordId, isBlocked: false });
      toast.success(`${activeTab === "partners" ? "Partner" : "Host"} restored/unblocked successfully!`);
      setShowRestoreModal(false);
      setRecordToRestore(null);
      if (activeTab === "partners") {
        fetchDisabledPartners();
      } else {
        fetchDisabledHosts();
      }
    } catch (error) {
      console.error("Error restoring record:", error);
      toast.error(error?.response?.data?.message || "Failed to restore account");
    } finally {
      setRestoreLoading(false);
    }
  };

  // Permanent Delete Handler
  const handleConfirmDelete = async () => {
    if (!recordToDelete) return;
    try {
      setDeleteLoading(true);
      const recordId = recordToDelete._id || recordToDelete.id;
      if (activeTab === "partners") {
        await hardDeletePartner(recordId);
        toast.success("Partner permanently hard deleted!");
        fetchDisabledPartners();
      } else {
        await hardDeleteUser(recordId);
        toast.success("Host account permanently hard deleted!");
        fetchDisabledHosts();
      }
      setShowDeleteModal(false);
      setRecordToDelete(null);
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error(error?.response?.data?.message || "Failed to delete account");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Pagination Components
  const partnerTotalPages = Math.ceil(partnerTotalCount / partnerItemsPerPage) || 1;
  const hostTotalPages = Math.ceil(hostTotalCount / hostItemsPerPage) || 1;

  const partnerPagination = (
    <Pagination
      pageCount={partnerTotalPages}
      pageValue={partnerPage}
      setPage={setPartnerPage}
      total={partnerTotalCount}
      pageSize={partnerItemsPerPage}
    />
  );

  const hostPagination = (
    <Pagination
      currentPage={hostPage}
      totalPages={hostTotalPages}
      onPageChange={(page) => setHostPage(page)}
    />
  );

  return (
    <div className="qx-page qx-partners-page mt-4">
      <PageTitle activeMenu="Delete Requests" motherMenu="Dashboard" />

      <Card className="qx-filter-card p-3 mb-3 border-0">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <div className="position-relative flex-grow-1" style={{ maxWidth: "420px", minWidth: "220px" }}>
            <input
              type="text"
              className="form-control"
              placeholder={activeTab === "partners" ? "Search partner by name, phone..." : "Search host by name, phone..."}
              value={activeTab === "partners" ? partnerSearch : hostSearch}
              onChange={(e) => {
                if (activeTab === "partners") setPartnerSearch(e.target.value);
                else setHostSearch(e.target.value);
              }}
            />
            {(activeTab === "partners" ? partnerSearch : hostSearch) && (
              <button
                type="button"
                onClick={() => {
                  if (activeTab === "partners") setPartnerSearch("");
                  else setHostSearch("");
                }}
                className="btn p-0 position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent"
                style={{ cursor: "pointer", color: "#667085" }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Start Date */}
          <div style={{ maxWidth: "150px", minWidth: "130px" }}>
            <Form.Control
              type="date"
              value={activeTab === "partners" ? partnerStartDate : hostStartDate}
              max={todayStr}
              onChange={activeTab === "partners" ? handlePartnerStartDateChange : handleHostStartDateChange}
              title="Start Date"
            />
          </div>

          {/* End Date */}
          <div style={{ maxWidth: "150px", minWidth: "130px" }}>
            <Form.Control
              type="date"
              value={activeTab === "partners" ? partnerEndDate : hostEndDate}
              min={activeTab === "partners" ? (partnerStartDate || undefined) : (hostStartDate || undefined)}
              max={todayStr}
              onChange={activeTab === "partners" ? handlePartnerEndDateChange : handleHostEndDateChange}
              title="End Date"
            />
          </div>

          {((activeTab === "partners" && (partnerStartDate || partnerEndDate || partnerSearch || partnerSortField !== "updatedAt" || partnerSortOrder !== "desc" || partnerSortSubscriptionId)) ||
            (activeTab === "hosts" && (hostStartDate || hostEndDate || hostSearch || hostSortBy !== "updatedAt" || hostSortOrder !== "desc"))) && (
              <Button
                variant="outline-danger"
                size="sm"
                className="d-flex align-items-center gap-1 px-2 fw-bold rounded-pill"
                style={{ height: "34px", fontSize: "0.75rem", borderColor: "#800001", color: "#800001" }}
                onClick={() => {
                  if (activeTab === "partners") {
                    setPartnerSearch("");
                    setPartnerStartDate("");
                    setPartnerEndDate("");
                    setPartnerSortField("updatedAt");
                    setPartnerSortSubscriptionId("");
                    setPartnerSortOrder("desc");
                    setPartnerPage(0);
                  } else {
                    setHostSearch("");
                    setHostStartDate("");
                    setHostEndDate("");
                    setHostSortBy("updatedAt");
                    setHostSortOrder("desc");
                    setHostPage(0);
                  }
                }}
              >
                <X size={13} /> Clear
              </Button>
            )}
        </div>
      </Card>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3 qx-partners-tabs"
      >
        <Tab eventKey="partners" title={`Disabled Partners (${partnerTotalCount})`}>
          <div className="mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <small className="qx-info-note mb-0 text-muted">
              <Info size={14} className="me-1" />
              Partners that are currently disabled
            </small>
          </div>

          {partnerLoading && (!partnerList || partnerList.length === 0) ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="danger" />
            </div>
          ) : partnerList && partnerList.length > 0 ? (
            <Col>
              <Card className="qx-table-card qx-partners-table-card position-relative">
                {partnerLoading && (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                    style={{ background: "rgba(255, 255, 255, 0.65)", zIndex: 10, borderRadius: "inherit" }}
                  >
                    <Spinner animation="border" variant="danger" />
                  </div>
                )}
                <Card.Body className="p-0">
                  <div
                    ref={tableScrollRef}
                    className="table-responsive"
                    onScroll={(e) => {
                      scrollPosRef.current = e.currentTarget.scrollLeft;
                    }}
                  >
                    <Table className="mb-0 align-middle">
                      <thead>
                        <tr>
                          <th>
                            <strong>S.NO</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handlePartnerHeaderSort("hostName")}>
                            <strong>Name {partnerSortField === "hostName" ? (partnerSortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handlePartnerHeaderSort("phoneNumber")}>
                            <strong>Phone {partnerSortField === "phoneNumber" ? (partnerSortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handlePartnerHeaderSort("partnerData.email")}>
                            <strong>Email {partnerSortField === "partnerData.email" ? (partnerSortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handlePartnerHeaderSort("partnerData.companyName")}>
                            <div><strong>Company</strong></div>
                            <small className="text-muted fw-semibold">
                              Name {partnerSortField === "partnerData.companyName" ? (partnerSortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </small>
                          </th>
                          {renderPlanHeaders()}
                          <th className="cursor-pointer" onClick={() => handlePartnerHeaderSort("partnerWhatsappMessageLimit")}>
                            <div><strong>WA Message</strong></div>
                            <small className="text-muted fw-semibold">
                              Consumed {partnerSortField === "partnerWhatsappMessageLimit" ? (partnerSortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </small>
                          </th>
                          <th className="cursor-pointer" onClick={() => handlePartnerHeaderSort("partnerRemainingWhatsappMessageLimit")}>
                            <div><strong>WA Message</strong></div>
                            <small className="text-muted fw-semibold">
                              Remaining {partnerSortField === "partnerRemainingWhatsappMessageLimit" ? (partnerSortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </small>
                          </th>
                          <th className="cursor-pointer" onClick={() => handlePartnerHeaderSort("createdAt")}>
                            <strong>Created at {partnerSortField === "createdAt" ? (partnerSortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handlePartnerHeaderSort("partnerData.approvedAt")}>
                            <strong>Approved At {partnerSortField === "partnerData.approvedAt" ? (partnerSortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handlePartnerHeaderSort("updatedAt")}>
                            <strong>Deleted At {partnerSortField === "updatedAt" ? (partnerSortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>
                          <th>
                            <strong>Action</strong>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {partnerList.map((partner, index) => (
                          <tr key={partner._id || partner.id || index}>
                            <td>{index + 1 + partnerPage * partnerItemsPerPage}</td>
                            <td>{partner.hostName || "-"}</td>
                            <td>{partner?.countryCode || "-"} {partner?.phoneNumber || "-"}</td>
                            <td>{partner?.partnerData?.email || "-"}</td>
                            <td>{partner?.partnerData?.companyName || "-"}</td>
                            {renderPlanCells(partner)}
                            <td>
                              {partner?.partnerWhatsappMessageLimit - partner?.partnerRemainingWhatsappMessageLimit || "0"}/{partner?.partnerWhatsappMessageLimit || "0"}
                            </td>
                            <td>
                              {partner?.partnerRemainingWhatsappMessageLimit || "0"}
                            </td>
                            <td>
                              {partner?.createdAt
                                ? new Date(partner.createdAt).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "long",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                })
                                : ""}
                            </td>
                            <td>
                              {partner?.partnerData?.approvedAt
                                ? new Date(partner?.partnerData?.approvedAt).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "long",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                })
                                : "-"}
                            </td>
                            <td>
                              {partner?.updatedAt
                                ? new Date(partner?.updatedAt).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "long",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                })
                                : "-"}
                            </td>
                            <td className="qx-table-actions-cell">
                              <TableActionsMenu
                                items={[
                                  {
                                    label: "View Details",
                                    onClick: () =>
                                      navigate(`/partner-detail/${partner?._id}`, {
                                        state: { partner, activeTab, currentPage: partnerPage },
                                      }),
                                  },
                                  {
                                    label: "Enable Partner",
                                    onClick: () => {
                                      setRecordToRestore(partner);
                                      setShowRestoreModal(true);
                                    },
                                  },
                                  {
                                    label: "Delete",
                                    danger: true,
                                    onClick: () => {
                                      setRecordToDelete(partner);
                                      setShowDeleteModal(true);
                                    },
                                  },
                                ]}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  {partnerPagination}
                </Card.Body>
              </Card>
            </Col>
          ) : (
            <div className="qx-empty-state qx-empty-state--rich">
              <p className="qx-empty-state__title">No partners found</p>
              <p className="qx-empty-state__desc mb-0">Try adjusting your filters or search.</p>
            </div>
          )}
        </Tab>

        {/* TAB 2: DISABLED HOSTS (Matches UserList.js) */}
        <Tab eventKey="hosts" title={`Disabled Hosts (${hostTotalCount})`}>
          {hostLoading && (!hostList || hostList.length === 0) ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="danger" />
            </div>
          ) : hostList && hostList.length > 0 ? (
            <Col>
              <Card className="qx-table-card border-0 position-relative">
                {hostLoading && (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                    style={{ background: "rgba(255, 255, 255, 0.65)", zIndex: 10, borderRadius: "inherit" }}
                  >
                    <Spinner animation="border" variant="danger" />
                  </div>
                )}
                <Card.Body className="p-0">
                  <div
                    ref={tableScrollRef}
                    className="table-responsive"
                    onScroll={(e) => {
                      scrollPosRef.current = e.currentTarget.scrollLeft;
                    }}
                  >
                    <Table hover className="mb-0 align-middle">
                      <thead>
                        <tr>
                          <th><strong>S.NO</strong></th>
                          <th
                            onClick={() => handleHostHeaderSort("hostName")}
                            style={{ cursor: "pointer", userSelect: "none" }}
                          >
                            <strong>
                              Name {hostSortBy === "hostName" ? (hostSortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </strong>
                          </th>
                          <th
                            onClick={() => handleHostHeaderSort("phoneNumber")}
                            style={{ cursor: "pointer", userSelect: "none" }}
                          >
                            <strong>
                              Phone Number {hostSortBy === "phoneNumber" ? (hostSortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </strong>
                          </th>
                          {hostUserType === "partnerHost" && (
                            <th><strong>Partner Name</strong></th>
                          )}
                          <th
                            onClick={() => handleHostHeaderSort("plan.name")}
                            style={{ cursor: "pointer", userSelect: "none" }}
                          >
                            <strong>
                              Plan {hostSortBy === "plan.name" ? (hostSortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </strong>
                          </th>
                          <th
                            onClick={() => handleHostHeaderSort("createdAt")}
                            style={{ cursor: "pointer", userSelect: "none" }}
                          >
                            <strong>
                              Registration Date {hostSortBy === "createdAt" ? (hostSortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </strong>
                          </th>
                          <th
                            onClick={() => handleHostHeaderSort("updatedAt")}
                            style={{ cursor: "pointer", userSelect: "none" }}
                          >
                            <strong>
                              Deleted At {hostSortBy === "updatedAt" ? (hostSortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </strong>
                          </th>

                          <th><strong>View</strong></th>

                          <th>
                            <strong>Action</strong>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {hostList.map((user, index) => (
                          <tr style={{ color: "#A60000" }} key={user._id || user.id || index}>
                            <td>{index + 1 + hostPage * hostItemsPerPage}</td>
                            <td>{user.name || user.hostName || "_"}</td>
                            <td>{`${user.countryCode || "+91"} ${user.phoneNumber || ""}`}</td>
                            {hostUserType === "partnerHost" && (
                              <td>
                                {user?.partnerId ? (
                                  <span
                                    className="fw-bold"
                                    style={{ cursor: "pointer", textDecoration: "underline", color: "blue" }}
                                    onClick={() => {
                                      const pId = typeof user.partnerId === "object" && user.partnerId !== null ? user.partnerId._id || user.partnerId.id : user.partnerId;
                                      if (pId) {
                                        navigate(`/partner-detail/${pId}`);
                                      }
                                    }}
                                  >
                                    {user?.partnerId?.hostName || user?.partnerId?.companyName || user?.partnerId?.name || "View Partner"}
                                  </span>
                                ) : (
                                  "-"
                                )}
                              </td>
                            )}
                            <td>{user?.plan?.name ? user?.plan?.name : "_"}</td>
                            <td>
                              {user?.createdAt
                                ? new Date(user.createdAt).toLocaleString("en-IN", {
                                  day: "2-digit",
                                  month: "long",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                })
                                : "_"}
                            </td>

                            <td>
                              {user?.updatedAt
                                ? new Date(user.updatedAt).toLocaleString("en-IN", {
                                  day: "2-digit",
                                  month: "long",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                })
                                : "_"}
                            </td>

                            <td
                              onClick={() => {
                                navigate(`/user-details/${user._id}`, {
                                  state: { user },
                                });
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <Eye size={16} className="eye-icon text-secondary" />
                            </td>
                            <td>
                              <Dropdown>
                                <Dropdown.Toggle
                                  variant="info light"
                                  className="light sharp btn btn-info i-false"
                                >
                                  {svg1}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    onClick={() => {
                                      setRecordToRestore(user);
                                      setShowRestoreModal(true);
                                    }}
                                  >
                                    Unblock
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => {
                                      setRecordToDelete(user);
                                      setShowDeleteModal(true);
                                    }}
                                  >
                                    Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  {hostPagination}
                </Card.Body>
              </Card>
            </Col>
          ) : (
            <div className="qx-empty-state qx-empty-state--rich text-center py-5">
              <p className="qx-empty-state__title fw-bold text-dark fs-5 mb-1">No disabled hosts found</p>
              <p className="qx-empty-state__desc mb-0 text-muted">Try adjusting your filters or search.</p>
            </div>
          )}
        </Tab>
      </Tabs>

      {/* CONFIRM RESTORE / UNBLOCK MODAL */}
      <Modal show={showRestoreModal} onHide={() => setShowRestoreModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-success">
            Unblock Account
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
          <p className="mb-0 fs-6">
            Are you sure you want to unblock the {activeTab === "partners" ? "partner" : "host"} account{" "}
            <strong>{recordToRestore?.hostName || recordToRestore?.name}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowRestoreModal(false)} disabled={restoreLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirmRestore} disabled={restoreLoading}>
            {restoreLoading ? <Spinner animation="border" size="sm" /> : "Confirm Unblock"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* CONFIRM DELETE MODAL */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-danger">
            Confirm Permanent Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
          <p className="text-danger fw-semibold mb-2">Warning: This action cannot be undone!</p>
          <p className="mb-0 text-secondary">
            Are you sure you want to permanently delete{" "}
            <strong>{recordToDelete?.hostName || recordToDelete?.name}</strong>?
            <br />
            <span style={{ color: '#A60100' }}>
              This will also permanently delete all events associated with this user.
            </span>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} disabled={deleteLoading}>
            {deleteLoading ? <Spinner animation="border" size="sm" /> : "Delete Permanently"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
